import { TPermissionCheckProps } from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.types';
import { TRbacConfig } from '@/shared/features/rbac/context/rbac-config-context.types';

// Every console check targets the Ident entity — scope a check to it.
const identCheck = (
  props: Omit<TPermissionCheckProps, 'entity'>,
): TPermissionCheckProps => ({ entity: 'Ident', ...props });

// Build an Ident check only when the subject exists, else undefined.
const identCheckWhen = <TSubject>(
  subject: TSubject | undefined,
  build: (subject: TSubject) => Omit<TPermissionCheckProps, 'entity'>,
): TPermissionCheckProps | undefined =>
  subject ? identCheck(build(subject)) : undefined;

/**
 * Console CASL permission checks consumed by the shared RBAC components via
 * `consoleRbacConfig.rbacPermissionChecks`. All scoped to the `Ident` entity.
 */
export const consoleRbacPermissionChecks: TRbacConfig['rbacPermissionChecks'] =
  {
    createUser: identCheck({ action: 'createUser' }),
    createRole: identCheck({ action: 'createUserRole' }),
    readUser: (userId) =>
      identCheckWhen(userId, (id) => ({ action: 'read', attribute: { id } })),
    userActions: (user) =>
      identCheckWhen(user, ({ userId, groupId }) => ({
        action: ['updateUser', 'deleteUser'],
        attribute: { id: userId, groupId },
      })),
    updateUser: (user) =>
      identCheckWhen(user, ({ userId, groupId }) => ({
        action: 'updateUser',
        attribute: { id: userId, groupId },
      })),
    deleteRoles: (roles) =>
      identCheckWhen(roles, (items) => ({
        action: 'deleteUserRole',
        attributes: items.map(({ userRoleId, groupId }) => ({
          id: userRoleId,
          groupId,
        })),
      })),
    deleteUsers: (users) =>
      identCheckWhen(users, (items) => ({
        action: 'deleteUser',
        attributes: items.map(({ userId, groupId }) => ({
          id: userId,
          groupId,
        })),
      })),
    updateRole: (role) =>
      identCheckWhen(role, ({ userRoleId, groupId }) => ({
        action: 'updateUserRole',
        attribute: { id: userRoleId, groupId },
      })),
    batchUpdateRoles: (groupId) =>
      identCheck({ action: 'updateUserRole', attribute: { groupId } }),
    batchUpdateUsers: (groupId) =>
      identCheck({ action: 'updateUser', attribute: { groupId } }),
  };
